import React from "react";
import { noop } from "lodash";
import { createPortal } from "react-dom";

import { Component } from "../../../../commons/types/component";
import { IconSmallAngleDown, IconSmallInputClear } from "../../../../resources/icons";
import cn from "../../libs/class-name";
import useOnMount from "../../libs/hooks/use-on-mount";
import Icon from "../Icon/Icon";

import SelectCtaButton from "./SelectCtaButton";
import SelectMenuItem, { ClickHandler } from "./SelectMenuItem";

interface Props<V> extends Component {
  children:
    | React.ReactElement<React.ComponentProps<typeof SelectMenuItem<V>>>
    | Array<React.ReactElement<React.ComponentProps<typeof SelectMenuItem<V>>>>;
  value?: any;
  name?: string;
  placeholder?: string;
  disabled?: boolean;
  error?: boolean;
  fixed?: boolean;
  icon?: React.ReactNode;
  ctaButtons?: (closePortal: () => void) => React.ReactElement<React.ComponentProps<typeof SelectCtaButton>>;
  onBlur?: (event: React.FocusEvent<HTMLButtonElement>) => void;
  onChange?: ClickHandler<V>;
  onResetSelection?: ClickHandler<V>;
  onClick?: ClickHandler<V>;
  withResetSelectionButton?: boolean;
  showName?: boolean;
  minContentWidthForMenuItems?: boolean;
}

function SelectField<V>({
  children,
  classNames = [],
  value = "",
  name = "",
  placeholder = "",
  disabled = false,
  error = false,
  fixed = false,
  icon = null,
  ctaButtons = undefined,
  onBlur = noop,
  onResetSelection = noop,
  onClick = noop,
  withResetSelectionButton = false,
  showName = false,
  minContentWidthForMenuItems = false,
  onChange
}: Props<V>) {
  const selectFieldRef = React.useRef<HTMLDivElement>(null);
  const resetButtonRef = React.useRef<HTMLButtonElement>(null);
  const flyoutRef = React.useRef<HTMLDivElement | null>(null);

  const [isFlyoutOpen, setIsFlyoutOpen] = React.useState(false);
  const [isOpenAbove, setIsOpenAbove] = React.useState(false);

  // The position is written straight to the DOM node rather than rendered through state: a state
  // update only paints a frame or two after the scroll event that caused it, which makes the flyout
  // visibly trail the select field while scrolling.
  const positionFlyout = React.useCallback(() => {
    const flyout = flyoutRef.current;
    const selectField = selectFieldRef.current;

    if (!flyout || !selectField) {
      return;
    }

    const selectSize = selectField.getBoundingClientRect();
    const resetButtonSize = resetButtonRef.current?.getBoundingClientRect();

    const width =
      withResetSelectionButton && resetButtonSize ? selectSize.width - resetButtonSize.width : selectSize.width;

    // Apply the width before measuring, so the height accounts for the resulting text wrapping.
    flyout.style.width = minContentWidthForMenuItems ? "" : `${width}px`;
    flyout.style.minWidth = minContentWidthForMenuItems ? `${width}px` : "";

    const flyoutSize = flyout.getBoundingClientRect();

    /**
     * Avoid issues for calculating the position, by keeping the responsibility for sizes & spacings for the flyout menu in here.
     * We define a similar spacing value here ($space-xxs) and use it to calculate the flyout position as a whole.
     * */
    const spaceXXS = 10;

    const openAbove = selectSize.top + selectSize.height + flyoutSize.height > window.innerHeight;

    flyout.style.top = `${
      openAbove
        ? selectSize.top - (flyoutSize.height + spaceXXS) + (fixed ? 0 : window.scrollY)
        : selectSize.top + (selectSize.height + spaceXXS) + (fixed ? 0 : window.scrollY)
    }px`;
    flyout.style.left = `${selectSize.left + (fixed ? 0 : window.scrollX)}px`;

    setIsOpenAbove(openAbove);
  }, [fixed, minContentWidthForMenuItems, withResetSelectionButton]);

  const flyoutRefCallback = React.useCallback(
    (node: HTMLDivElement | null) => {
      flyoutRef.current = node;

      if (node) {
        positionFlyout();
      }
    },
    [positionFlyout]
  );

  const openFlyout = React.useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    // Keeps the document click listener below from closing the flyout in the same tick.
    event.nativeEvent.stopImmediatePropagation();
    setIsFlyoutOpen(true);
  }, []);

  const closeFlyout = React.useCallback(() => {
    setIsFlyoutOpen(false);
    setIsOpenAbove(false);
  }, []);

  useOnMount(() => {
    // `closeFlyout` is stable, so capturing both handlers once on mount is safe.
    const onGlobalKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closeFlyout();
      }
    };

    const onGlobalClick = (event: MouseEvent) => {
      if (flyoutRef.current && !flyoutRef.current.contains(event.target as Node)) {
        closeFlyout();
      }
    };

    document.addEventListener("keydown", onGlobalKeyDown, false);
    document.addEventListener("click", onGlobalClick, false);

    return () => {
      document.removeEventListener("keydown", onGlobalKeyDown, false);
      document.removeEventListener("click", onGlobalClick, false);
    };
  });

  // The flyout is positioned against the document, so it has to follow the select field whenever it
  // moves. `scroll` does not bubble, so it is observed in the capture phase to also catch scrollable
  // ancestors instead of only the window.
  React.useEffect(() => {
    if (!isFlyoutOpen) {
      return;
    }

    document.addEventListener("scroll", positionFlyout, true);
    window.addEventListener("resize", positionFlyout);

    return () => {
      document.removeEventListener("scroll", positionFlyout, true);
      window.removeEventListener("resize", positionFlyout);
    };
  }, [isFlyoutOpen, positionFlyout]);

  const handleFlyoutItemClick = React.useCallback(
    (value: any) => {
      onChange?.(value);
      closeFlyout();
    },
    [closeFlyout, onChange]
  );

  const label = React.useMemo(() => {
    let foundLabel: React.ReactNode = null;

    React.Children.forEach(children, (item: React.ReactElement<React.ComponentProps<typeof SelectMenuItem<V>>>) => {
      if (item.props.value === value) {
        foundLabel = item.props.label;
      }
    });

    return foundLabel;
  }, [children, value]);

  return (
    <>
      <div
        ref={selectFieldRef}
        className={cn(
          "SelectField",
          [
            {
              selected: !!value,
              disabled,
              error,
              isOpen: isFlyoutOpen,
              isOpenAbove,
              withResetSelectionButton,
              withIcon: !!icon,
              showName
            }
          ],
          classNames
        )}
        onClick={() => onClick?.(value)}
      >
        <button
          name={name}
          type="button"
          className="SelectField__button"
          onClick={event => (isFlyoutOpen ? closeFlyout() : openFlyout(event))}
          onBlur={onBlur}
          disabled={disabled}
        >
          {icon && <span className="SelectField__icon">{icon}</span>}
          {value ? label : <span className="SelectField__placeholder">{placeholder}</span>}
          <span className="SelectField__arrow">
            <Icon source={IconSmallAngleDown} />
          </span>
        </button>

        {showName && <div className="SelectField__name">{name}</div>}

        {withResetSelectionButton && (
          <button
            ref={resetButtonRef}
            onClick={() => onResetSelection?.(value)}
            className="SelectField__reset-button"
            type="button"
          >
            <Icon source={IconSmallInputClear} />
          </button>
        )}
      </div>

      {isFlyoutOpen &&
        createPortal(
          <div ref={flyoutRefCallback} className={cn("SelectField__flyout", [{ fixed, isOpenAbove }])}>
            {ctaButtons && <div className="SelectField__ctaButtons">{ctaButtons(closeFlyout)}</div>}
            <ul className="SelectField__menu">
              {React.Children.map(
                children,
                (item: React.ReactElement<React.ComponentProps<typeof SelectMenuItem<V>>>, index) => (
                  <li key={index}>
                    {React.cloneElement(item, {
                      onClick: handleFlyoutItemClick,
                      selected: value && item.props.value === value
                    })}
                  </li>
                )
              )}
            </ul>
          </div>,
          window.document.body
        )}
    </>
  );
}

export default SelectField;
